import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, AlertCircle, Copy, ExternalLink } from "lucide-react";

/**
 * Small settings panel to configure the Resend webhook secret directly from
 * the CRM. Used when the deployment platform doesn't expose env-var editing.
 */
export default function EmailTrackingSettings() {
  const { t } = useI18n();
  const [status, setStatus] = useState({ configured: false, source: null, hint: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [secret, setSecret] = useState("");

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-tracking/settings");
      setStatus(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const save = async () => {
    if (!secret.startsWith("whsec_")) {
      toast.error(t("tracking_settings_invalid_secret"));
      return;
    }
    setSaving(true);
    try {
      await api.put("/email-tracking/settings", { resend_webhook_secret: secret });
      toast.success(t("tracking_settings_saved"));
      setSecret("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("tracking_settings_save_error"));
    } finally { setSaving(false); }
  };

  const webhookUrl = `${window.location.origin}/api/webhooks/resend`;
  const copyUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success(t("tracking_settings_url_copied"));
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-indigo-100 p-2">
            <KeyRound className="h-5 w-5 text-indigo-700" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-slate-900">{t("tracking_settings_title")}</h3>
            <p className="text-sm text-slate-500">{t("tracking_settings_desc")}</p>
          </div>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          ) : status.configured ? (
            <Badge className="bg-emerald-100 text-emerald-700 flex items-center gap-1" data-testid="tracking-status-configured">
              <CheckCircle2 className="h-3 w-3" />{t("tracking_settings_active")}
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1" data-testid="tracking-status-missing">
              <AlertCircle className="h-3 w-3" />{t("tracking_settings_not_configured")}
            </Badge>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-slate-50 rounded-md border p-3 text-sm space-y-2.5">
          <div className="font-medium text-slate-800">{t("tracking_settings_steps_title")}</div>
          <ol className="space-y-2 text-slate-600 list-decimal ml-5">
            <li>
              {t("tracking_settings_step1")}{" "}
              <a href="https://resend.com/webhooks" target="_blank" rel="noreferrer" className="text-brand-blue hover:underline inline-flex items-center gap-0.5">
                resend.com/webhooks <ExternalLink className="h-3 w-3" />
              </a>
            </li>
            <li>
              {t("tracking_settings_step2")}
              <div className="mt-1 flex items-center gap-2">
                <code className="bg-white border rounded px-2 py-1 text-xs break-all flex-1" data-testid="tracking-webhook-url">{webhookUrl}</code>
                <Button size="sm" variant="outline" onClick={copyUrl} data-testid="tracking-copy-url"><Copy className="h-3 w-3" /></Button>
              </div>
            </li>
            <li>{t("tracking_settings_step3")}</li>
            <li>{t("tracking_settings_step4")}</li>
          </ol>
        </div>

        {/* Save form */}
        <div className="space-y-2">
          <Label>{t("tracking_settings_secret_label")}</Label>
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder="whsec_..."
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              data-testid="tracking-secret-input"
            />
            <Button
              onClick={save}
              disabled={saving || !secret.trim()}
              className="bg-brand-blue"
              data-testid="tracking-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("tracking_settings_save")}
            </Button>
          </div>
          {status.configured && status.hint && (
            <p className="text-xs text-slate-500">
              {t("tracking_settings_currently").replace("{source}", status.source === "env" ? t("tracking_settings_source_env") : t("tracking_settings_source_db"))}
              <span className="font-mono ml-1">{status.hint}</span>
            </p>
          )}
        </div>

        {/* Maintenance section */}
        <div className="pt-4 border-t space-y-3">
          <h4 className="text-sm font-semibold text-slate-800">{t("maintenance_title")}</h4>
          <OrphanPaymentsCleaner />
        </div>
      </CardContent>
    </Card>
  );
}

function OrphanPaymentsCleaner() {
  const { t } = useI18n();
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/payments/cleanup-orphans");
      setLastResult(data);
      if (data.removed > 0) toast.success(t("orphans_removed").replace("{n}", data.removed));
      else toast.info(t("orphans_none"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("orphans_error"));
    } finally { setRunning(false); }
  };
  return (
    <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
      <div className="flex-1">
        <div className="text-sm font-medium text-amber-900">{t("orphans_title")}</div>
        <div className="text-xs text-amber-800 mt-0.5">{t("orphans_desc")}</div>
        {lastResult && (
          <div className="text-xs text-slate-600 mt-1.5">{t("orphans_last")}: <strong>{lastResult.removed}</strong></div>
        )}
      </div>
      <Button size="sm" variant="outline" onClick={run} disabled={running} className="shrink-0" data-testid="cleanup-orphans-btn">
        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : t("orphans_run")}
      </Button>
    </div>
  );
}
